import React, { useEffect } from 'react';
import { SubscriptionUpdatePaymentModal } from '@nexus/fff-payments';
import { useUserContext } from '@nexus/fff-utilities';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { trackEvent, trackPageLoad } from '@nexus/fff-analytics';

import Layout from '../../components/Layout';
import DesktopCards from './DesktopCards';
import MobileTable from './MobileTable';
import Notice from './Notice';
import Customize from './Customize';
import UpgradeButton from './UpgradeButton';
import CouponSelector from './CouponSelector';
import { couponScreenKey, Plans } from '../constants';
import { getImagesStaticUrl, searchToObject } from '../../helpers';
import useLaunchDarkly from '../../hooks/useLaunchDarkly';
import useShowErrorOrRedirectThankYouPage from '../../hooks/useShowPayErrorOrThankYouPage';
import useCouponContent from '../../hooks/useCouponContent';
import useCouponSelector from '../../hooks/useCouponSelector';
import './styles.css';

export const Upgrade = ({
  history, flags, ldClient, location: { pathname, search },
}) => {
  const { userInfo } = useUserContext();
  useLaunchDarkly(ldClient, userInfo);

  const { displayAdditionalPerks } = flags || {};
  const { coupon_code: couponCode } = searchToObject(search);
  const isSeasonal = userInfo ? userInfo.user && userInfo.user.subscriptionType === 'SEASONAL'
      && userInfo.user.subscriptionState === 'ACTIVE' : false;

  const thanksPage = pathname && pathname.indexOf('/my-account-v3/') === -1
    ? '/upgrade-thanks'
    : '/my-account-v3/upgrade-thanks';

  const { getCouponContentKey, handleCouponChange } = useCouponContent(couponScreenKey.UPGRADE);
  const {
    handleApplyCoupon,
    handleRemoveCoupon,
    couponsApplied,
    couponError,
  } = useCouponSelector(handleCouponChange);
  const {
    showPayment,
    errorPaymentMessage,
    changeSubscription,
    onClose,
    isLoading,
  } = useShowErrorOrRedirectThankYouPage(history, thanksPage, Plans.SEASONAL, couponsApplied);

  useEffect(() => {
    if (isSeasonal) {
      trackPageLoad({
        pathName: '/upgrade-confirmation',
        properties: {
          title: 'Upgrade Page',
          coupon_code: couponCode,
        },
      });
    }
  }, []);

  if (!isSeasonal) {
    window.location.replace(process.env.REACT_APP_SITE_URL);
    return null;
  }

  const handleChangeSubscription = () => {
    trackEvent({
      ctaName: 'upgrade-confirmation',
      properties: {
        upgrading_plan: Plans.ANNUAL,
        CTA: 'upgrade-confirmation',
      },
    }).then();
    changeSubscription(Plans.ANNUAL);
  };

  return (
    <Layout isLoading={isLoading}>
      <div id="content" className="site-content">
        <div
          className="upgrade-background-banner"
          style={{
            backgroundImage: `url(${getImagesStaticUrl(
              getCouponContentKey('upgradeBackgroundBannerImage', 'hero-desktop.png'),
            )
            })`,
          }}
        >
          <img
            className="background-banner-wave"
            src={getImagesStaticUrl(
              getCouponContentKey('backgroundBannerWaveImage', 'upgrade-wave.svg'),
            )}
            alt="background"
          />
        </div>
        <div className="upgrade-row upgrade-container" style={{ position: 'relative' }}>
          <div className="upgrade-col padding-none">
            <div className="page-content">
              <article className="page">
                <div className="upgrade-confirmation-container">
                  <div className="head text-center">
                    {getCouponContentKey('title', 'Upgrade Now')}
                  </div>
                  <div className="sub-head text-center">
                    {getCouponContentKey('subtitle', 'Upgrade to an Annual Membership Today!')}
                  </div>
                  <DesktopCards
                    showAdditionalPerks={displayAdditionalPerks}
                    getCouponContentKey={getCouponContentKey}
                  />
                  <MobileTable
                    showAdditionalPerks={displayAdditionalPerks}
                    getCouponContentKey={getCouponContentKey}
                  />
                  <CouponSelector
                    plan={Plans.ANNUAL}
                    onApplyCoupon={handleApplyCoupon}
                    onRemoveCoupon={handleRemoveCoupon}
                    initialCouponCode={couponCode}
                    error={couponError}
                    couponsApplied={couponsApplied}
                  />
                  <div className="acceptance-container">
                    <Notice getCouponContentKey={getCouponContentKey} />
                    <UpgradeButton
                      onClickUpgrade={handleChangeSubscription}
                      getCouponContentKey={getCouponContentKey}
                    />
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
        <hr className="upgrade-divider" />
        <Customize
          onClickUpgrade={handleChangeSubscription}
          getCouponContentKey={getCouponContentKey}
        />
      </div>
      <SubscriptionUpdatePaymentModal
        onClose={onClose}
        subtitle={errorPaymentMessage}
        isOpen={showPayment}
        onSuccess={handleChangeSubscription}
        hasError={!!errorPaymentMessage}
      />
    </Layout>
  );
};

Upgrade.defaultProps = {
  history: {},
  flags: {},
  ldClient: null,
  location: { search: '' },
};

export default withLDConsumer()(Upgrade);
